import { isLikelyRdTitle } from "./rdTitleMatch.js";
import { RD_MAX_RATE, RD_MIN_RATE } from "../data/companyTeamDemo.js";

export const EMPLOYMENT_TYPES = [
  { id: "employee", label: "Employee" },
  { id: "contractor", label: "Contractor" },
];

export const DEFAULT_EMPLOYMENT_TYPE = "employee";

/** Flat market-salary fallback when Clay/enrichment leaves Salary empty. */
export const DEFAULT_MARKET_SALARY = 100000;

/** SR&ED-style claim proxy applied on top of salary × % R&D time. */
export const EMPLOYEE_CLAIM_RATE = 0.6;
export const CONTRACTOR_CLAIM_RATE = 0.3;

/** Midpoint of the likely R&D title band — used only as a suggested % of time. */
export const DEFAULT_RD_PCT_FOR_RD_TITLE = Math.round(
  ((RD_MIN_RATE + RD_MAX_RATE) / 2) * 100
);

export function isCompanyInCanada(companyOrCountry) {
  const country =
    typeof companyOrCountry === "string"
      ? companyOrCountry
      : companyOrCountry?.fields?.Country || companyOrCountry?.Country || "";
  const normalized = String(country).trim().toLowerCase();
  return (
    normalized === "canada" ||
    normalized === "ca" ||
    normalized === "can" ||
    normalized.includes("canada")
  );
}

export function isLocationInCanada(location) {
  const normalized = String(location || "").trim().toLowerCase();
  if (!normalized) return null;
  if (
    normalized === "outside canada" ||
    normalized === "outside ca" ||
    normalized === "non-canada" ||
    normalized === "international"
  ) {
    return false;
  }
  return (
    normalized === "canada" ||
    normalized === "ca" ||
    normalized === "can" ||
    normalized.includes("canada")
  );
}

export function locationLabelFromInCanada(inCanada) {
  return inCanada ? "Canada" : "Outside Canada";
}

/**
 * Suggest % of time on R&D from title matching.
 * Likely R&D titles default to the midpoint of the 30–65% band; others default to 0.
 */
export function suggestRdPct(title) {
  return isLikelyRdTitle(title) ? DEFAULT_RD_PCT_FOR_RD_TITLE : 0;
}

export function suggestRdPctHint(title) {
  if (isLikelyRdTitle(title)) {
    return `Suggested ${DEFAULT_RD_PCT_FOR_RD_TITLE}% of time from title match. Edit if you know actual time.`;
  }
  return "Suggested 0% — title is not a likely R&D role. Raise if they do eligible work.";
}

export function salarySourceHint(person) {
  if (person?.salarySource === "manual") return "Edited manually";
  if (person?.salarySource === "default") return "Default $100k — edit if you have a better estimate";
  if (person?.salarySource === "estimate") return "Estimated from title / location";
  if (person?.salarySource === "peer") return "Matched from a teammate with a similar title";
  if (person?.salary != null) return "From Clay / title market estimate";
  return "Enter market salary or re-index from Clay";
}

export function claimRateForEmploymentType(employmentType) {
  return employmentType === "contractor"
    ? CONTRACTOR_CLAIM_RATE
    : EMPLOYEE_CLAIM_RATE;
}

/**
 * Allocated $ =
 *   employee:   up to 60% × salary × (PercentR&D / 100)
 *   contractor: up to 30% × salary × (PercentR&D / 100)
 */
export function calculateAllocatedRd({
  salary,
  rdPct,
  employmentType = DEFAULT_EMPLOYMENT_TYPE,
} = {}) {
  const salaryNum = salary != null && salary !== "" ? Number(salary) : NaN;
  const pct = rdPct != null && rdPct !== "" ? Number(rdPct) : NaN;
  if (!Number.isFinite(salaryNum) || !Number.isFinite(pct)) return null;
  const claimRate = claimRateForEmploymentType(employmentType);
  return claimRate * salaryNum * (Math.max(0, Math.min(100, pct)) / 100);
}

export function allocatedFormulaHint(employmentType) {
  const pct = Math.round(claimRateForEmploymentType(employmentType) * 100);
  return employmentType === "contractor"
    ? `up to ${pct}% × salary × % R&D`
    : `up to ${pct}% × salary × % R&D`;
}

export function applyAllocationDefaults(person, { companyInCanada = false } = {}) {
  const title = person.title || "";
  const titleIsRd = isLikelyRdTitle(title);
  const isRd =
    person.isRdManual && typeof person.isRd === "boolean" ? person.isRd : titleIsRd;
  const rdPct =
    person.rdPct != null && person.rdPct !== ""
      ? Number(person.rdPct)
      : suggestRdPct(title);
  const locationInCanada = isLocationInCanada(person.location);
  const inCanada =
    typeof person.inCanada === "boolean"
      ? person.inCanada
      : locationInCanada != null
      ? locationInCanada
      : Boolean(companyInCanada);
  const employmentType = person.employmentType || DEFAULT_EMPLOYMENT_TYPE;
  const parsedSalary =
    person.salary != null && person.salary !== "" ? Number(person.salary) : NaN;
  const hasSalary = Number.isFinite(parsedSalary) && parsedSalary > 0;
  const salary = hasSalary ? parsedSalary : DEFAULT_MARKET_SALARY;
  const salarySource = hasSalary
    ? person.salarySource || "enrichment"
    : person.salarySource === "manual"
    ? "manual"
    : "default";
  const normalizedRdPct = Number.isFinite(rdPct)
    ? Math.max(0, Math.min(100, rdPct))
    : 0;

  return {
    ...person,
    isRd,
    isRdManual: Boolean(person.isRdManual),
    matchedTitle: person.matchedTitle || (isRd ? title : null),
    inCanada,
    location: person.location || locationLabelFromInCanada(inCanada),
    employmentType,
    rdPct: normalizedRdPct,
    salary,
    salarySource,
    needsSalaryBackfill: !hasSalary,
    claimRate: claimRateForEmploymentType(employmentType),
    allocatedRd: calculateAllocatedRd({
      salary,
      rdPct: normalizedRdPct,
      employmentType,
    }),
  };
}
